import React, { useState, useContext, useCallback, useEffect, useRef } from 'react'
import styles from './MediaView.module.scss'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import {
  LIST_MEDIA_COLLECTIONS_QUERY,
  GET_MEDIA_COLLECTION_QUERY,
  CREATE_MEDIA_COLLECTION_MUTATION,
  CHECK_OUT_MEDIA_COLLECTION_MUTATION,
  CHECK_IN_MEDIA_COLLECTION_MUTATION,
  DELETE_MEDIA_COLLECTION_MUTATION,
  UPDATE_MEDIA_COLLECTION_DATE_MUTATION,
  BROWSE_MEDIA_FOLDER_QUERY,
  GET_PROJECTS_QUERY
} from '../query/gqlQueries'
import UserContext from '../context/UserContext'

import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import LockIcon from '@mui/icons-material/Lock'
import FolderIcon from '@mui/icons-material/Folder'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary'

import MediaLibrary from './MediaLibrary'

// Human-friendly byte formatter: 1234567890 → "1.15 GB"
function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 2)} ${units[i]}`
}

// Mirror the backend slugify so the folder preview matches what'll be created.
function slugifyPreview(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 80)
}

// Group a flat list of shoots by their parent project title, preserving the
// server's ordering (project ASC, createdAt DESC). Shoots with no project
// (legacy data) fall under "Ungrouped".
function groupShootsByProject(shoots) {
  const groups = []
  const index = {}
  for (const shoot of shoots) {
    const project = shoot.properties.project || 'Ungrouped'
    if (!(project in index)) {
      index[project] = groups.length
      groups.push({ project, shoots: [] })
    }
    groups[index[project]].shoots.push(shoot)
  }
  return groups
}

// The GraphQL server bakes an absolute thumbnail URL using ITS
// IMAGE_STORE_PUBLIC_URL (which may be a stale 'localhost:4001'). Rewrite the
// origin to the frontend's REACT_APP_IMAGE_STORE_URI when set, so each client
// can point at the reachable image-server (LAN IP, public tunnel, …) without
// the backend having to know which client is asking. Keeps the /media/…
// path intact. No-op when the env var is unset or the URL can't be parsed.
const IMAGE_STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI
const STORE_TOKEN = process.env.REACT_APP_IMAGE_STORE_TOKEN || ''
function thumbSrc(url) {
  if (!url) return url
  if (!IMAGE_STORE_URI) return url
  try {
    const u = new URL(url)
    return IMAGE_STORE_URI.replace(/\/$/, '') + u.pathname + u.search
  } catch {
    return url
  }
}

// Today as YYYY-MM-DD for the date picker default.
function todayISO() {
  return new Date().toISOString().split('T')[0]
}

// A YYYY-MM-DD (or ISO/Neo4j) shoot date → "11 Mar 2026". Empty → null.
function formatShootDate(val) {
  if (!val) return null
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [y, m, d] = val.split('-').map(Number)
      return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    }
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch { return null }
}

// "2h ago", "yesterday", "Mar 11", "2024-03-11"
function formatRelative(iso) {
  if (!iso) return null
  let d
  try { d = new Date(iso) } catch (e) { return null }
  if (isNaN(d.getTime())) return null
  const now = Date.now()
  const ms = now - d.getTime()
  const s = Math.floor(ms / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  if (s < 86400 * 2) return 'yesterday'
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' })
}

export default function MediaView({ setIsVisible }) {
  const { username } = useContext(UserContext)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  // Folder drill-down: null = project detail, "RAW"|"PSD"|… = browsing that folder.
  const [selectedFolder, setSelectedFolder] = useState(null)
  // Lightbox: index into the current folder's photos, or null when closed.
  const [previewIdx, setPreviewIdx] = useState(null)
  // Web upload of files into the current folder.
  const [uploading, setUploading] = useState(false)
  const uploadInputRef = useRef(null)
  const [isCreating, setIsCreating] = useState(false)
  const [newName, setNewName] = useState('')        // shoot name, e.g. "Audi A3"
  const [newProject, setNewProject] = useState('')  // parent project, e.g. "CleverCar"
  const [newNotes, setNewNotes] = useState('')
  const [newShootDate, setNewShootDate] = useState(todayISO())
  const [createError, setCreateError] = useState(null)
  // Inline shoot-date editing in the detail view.
  const [editingDate, setEditingDate] = useState(false)
  const [localDate, setLocalDate] = useState('')

  // Existing task-management projects, used to populate the project picker
  // so media shoots attach to the same projects the rest of the app uses.
  const { data: projectsData } = useQuery(GET_PROJECTS_QUERY, {
    variables: { username },
    skip: !username
  })
  const existingProjects = projectsData?.getProjects || []

  const { data: listData, loading: listLoading, refetch: refetchList } = useQuery(
    LIST_MEDIA_COLLECTIONS_QUERY,
    { variables: { username: null }, skip: !username, fetchPolicy: 'cache-and-network' }
  )

  const [fetchDetail, { data: detailData, loading: detailLoading, refetch: refetchDetail }] =
    useLazyQuery(GET_MEDIA_COLLECTION_QUERY, { fetchPolicy: 'cache-and-network' })

  const [fetchBrowse, { data: browseData, loading: browseLoading, refetch: refetchBrowse }] =
    useLazyQuery(BROWSE_MEDIA_FOLDER_QUERY, { fetchPolicy: 'cache-and-network' })

  const [createProject, { loading: creating }] = useMutation(CREATE_MEDIA_COLLECTION_MUTATION)
  const [checkOutProject] = useMutation(CHECK_OUT_MEDIA_COLLECTION_MUTATION)
  const [checkInProject] = useMutation(CHECK_IN_MEDIA_COLLECTION_MUTATION)
  const [deleteProject] = useMutation(DELETE_MEDIA_COLLECTION_MUTATION)
  const [updateShootDate] = useMutation(UPDATE_MEDIA_COLLECTION_DATE_MUTATION)

  const projects = listData?.listMediaCollections || []
  // Contents of the currently-open folder path — subfolders to drill into and
  // image files to show. Hoisted so the lightbox keyboard handler reaches them.
  const browse = browseData?.browseMediaFolder
  const photos = browse?.photos || []
  const browseFolders = browse?.folders || []

  // Lightbox keyboard nav: ← → to move, Esc to close.
  useEffect(() => {
    if (previewIdx === null) return
    const onKey = (e) => {
      if (e.key === 'Escape') setPreviewIdx(null)
      else if (e.key === 'ArrowRight') setPreviewIdx((i) => (i === null ? i : Math.min(i + 1, photos.length - 1)))
      else if (e.key === 'ArrowLeft') setPreviewIdx((i) => (i === null ? i : Math.max(i - 1, 0)))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [previewIdx, photos.length])

  const handleSelect = useCallback((id) => {
    setSelectedId(id)
    fetchDetail({ variables: { identity: id } })
  }, [fetchDetail])

  const handleBack = () => {
    setSelectedId(null)
    setSelectedFolder(null)
    setIsCreating(false)
    setCreateError(null)
    setNewName('')
    setNewProject('')
    setNewNotes('')
  }

  // Drill into a folder of a project — fetches its photos and triggers
  // server-side thumbnail caching as the grid renders.
  const handleOpenFolder = useCallback((folderPath) => {
    setSelectedFolder(folderPath)
    setPreviewIdx(null)
    fetchBrowse({ variables: { projectIdentity: selectedId, path: folderPath } })
  }, [selectedId, fetchBrowse])

  // Upload files straight into the current folder on the mini's drive. The
  // target path (project/collection/folder) is passed to the image-server,
  // which streams each file to disk keeping its original name.
  const handleUpload = useCallback(async (fileList) => {
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    const collProps = detailData?.getMediaCollection?.project?.properties
    if (!collProps) return
    if (!IMAGE_STORE_URI) {
      window.alert('REACT_APP_IMAGE_STORE_URI is not set — cannot reach the media server.')
      return
    }
    const rel = `${collProps.projectSlug}/${collProps.slug}${selectedFolder ? '/' + selectedFolder : ''}`
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    setUploading(true)
    try {
      const res = await fetch(`${IMAGE_STORE_URI.replace(/\/$/, '')}/media/upload?path=${encodeURIComponent(rel)}`, {
        method: 'POST',
        headers: STORE_TOKEN ? { Authorization: `Bearer ${STORE_TOKEN}` } : {},
        body: form,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `Upload failed (HTTP ${res.status})`)
      }
      await fetchBrowse({ variables: { projectIdentity: selectedId, path: selectedFolder } })
    } catch (e) {
      console.error('Upload failed:', e)
      window.alert(e.message?.includes('Failed to fetch') ? 'Could not reach the media server (is the tunnel up?).' : (e.message || 'Upload failed'))
    } finally {
      setUploading(false)
      if (uploadInputRef.current) uploadInputRef.current.value = ''
    }
  }, [detailData, selectedFolder, selectedId, fetchBrowse])

  // Back from the photo grid → project detail. Different from handleBack
  // (which exits all the way to the project list).
  const handleBackToProject = () => {
    setSelectedFolder(null)
    setPreviewIdx(null)
  }

  const handleCreate = useCallback(async (e) => {
    e?.preventDefault?.()
    if (!newName.trim() || !newProject.trim() || !username) return
    setCreateError(null)
    try {
      const { data } = await createProject({
        variables: {
          input: {
            name: newName.trim(),
            project: newProject.trim(),
            username,
            notes: newNotes.trim() || null,
            shootDate: newShootDate || null
          }
        }
      })
      const created = data?.createMediaCollection
      setIsCreating(false)
      setNewName('')
      setNewProject('')
      setNewNotes('')
      setNewShootDate(todayISO())
      await refetchList()
      if (created?.identity) handleSelect(created.identity)
    } catch (err) {
      setCreateError(err.message || 'Could not create shoot.')
    }
  }, [newName, newProject, newNotes, newShootDate, username, createProject, refetchList, handleSelect])

  const handleSaveDate = useCallback(async (id) => {
    if (!localDate) { setEditingDate(false); return }
    try {
      await updateShootDate({ variables: { identity: id, shootDate: localDate } })
      setEditingDate(false)
      await refetchDetail({ identity: id })
      await refetchList()
    } catch (err) {
      console.warn('updateShootDate failed', err)
    }
  }, [localDate, updateShootDate, refetchDetail, refetchList])

  const handleCheckOut = useCallback(async (id) => {
    if (!username) return
    try {
      await checkOutProject({ variables: { identity: id, username } })
      await refetchDetail({ identity: id })
      await refetchList()
    } catch (err) {
      console.warn('checkOut failed', err)
    }
  }, [username, checkOutProject, refetchDetail, refetchList])

  const handleCheckIn = useCallback(async (id) => {
    try {
      await checkInProject({ variables: { identity: id } })
      await refetchDetail({ identity: id })
      await refetchList()
    } catch (err) {
      console.warn('checkIn failed', err)
    }
  }, [checkInProject, refetchDetail, refetchList])

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm(
      'Delete this shoot from the registry?\n\n' +
      'Files on the external drive will NOT be touched — only the metadata + check-out state is removed. ' +
      'You can recreate the shoot with the same name to re-attach it.'
    )) return
    try {
      await deleteProject({ variables: { identity: id } })
      setSelectedId(null)
      await refetchList()
    } catch (err) {
      console.warn('delete failed', err)
    }
  }, [deleteProject, refetchList])

  if (!username) {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>
        <div className={styles.status}>Sign in to view projects.</div>
      </div>
    )
  }

  // ───────────────────── LIBRARY (originals, store-once) ─────────────────
  if (libraryOpen) {
    return (
      <MediaLibrary
        username={username}
        onBack={() => setLibraryOpen(false)}
        setIsVisible={setIsVisible}
      />
    )
  }

  // ───────────────────── PHOTO GRID (folder browse) ─────────────────────
  if (selectedId && selectedFolder && detailData?.getMediaCollection) {
    const projectName = detailData.getMediaCollection.project.properties.name

    return (
      <div className={styles.view}>
        <div className={styles.gridBg} />
        <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>

        <div className={styles.container}>
          <header className={styles.header}>
            <div className={styles.breadcrumbs}>
              <button className={styles.crumb} onClick={handleBack}>Shoots</button>
              <span className={styles.crumbSep}>/</span>
              <button className={styles.crumb} onClick={handleBackToProject}>{projectName}</button>
              {selectedFolder.split('/').map((seg, i, arr) => {
                const cumulative = arr.slice(0, i + 1).join('/')
                const isLast = i === arr.length - 1
                return (
                  <React.Fragment key={cumulative}>
                    <span className={styles.crumbSep}>/</span>
                    {isLast
                      ? <span className={styles.crumbCurrent}>{seg}</span>
                      : <button className={styles.crumb} onClick={() => handleOpenFolder(cumulative)}>{seg}</button>}
                  </React.Fragment>
                )
              })}
            </div>
            <div className={styles.toolbar}>
              <input
                ref={uploadInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleUpload(e.target.files)}
              />
              <button
                className={`${styles.iconBtn} ${styles.primaryBtn}`}
                title={`Upload files into ${selectedFolder}`}
                disabled={uploading}
                onClick={() => uploadInputRef.current?.click()}>
                <FileUploadIcon fontSize="small" /> {uploading ? 'Uploading…' : 'Upload'}
              </button>
              <button
                className={styles.iconBtn}
                title="Reload"
                onClick={() => fetchBrowse({ variables: { projectIdentity: selectedId, path: selectedFolder } })}>
                <RefreshIcon fontSize="small" />
              </button>
            </div>
          </header>

          {browseLoading && browseFolders.length === 0 && photos.length === 0 && (
            <div className={styles.status}>Scanning folder…</div>
          )}

          {browseFolders.length > 0 && (
            <div className={styles.folderGrid}>
              {browseFolders.map(f => (
                <div
                  key={f.path}
                  className={`${styles.folderCard} ${styles.folderCardBrowsable}`}
                  onClick={() => handleOpenFolder(f.path)}
                  title={`Open ${f.name}`}
                >
                  <div className={styles.folderHeader}>
                    <FolderIcon fontSize="small" style={{ color: '#94a3b8' }} />
                    <span className={styles.folderName}>{f.name}</span>
                  </div>
                  <div className={styles.folderStats}>
                    <div className={styles.statLine}>
                      <span className={styles.statNumber}>{f.fileCount.toLocaleString()}</span>
                      <span className={styles.statUnit}>files</span>
                    </div>
                    <div className={styles.statLine}>
                      <span className={styles.statNumber}>{formatBytes(f.totalBytes)}</span>
                    </div>
                    <div className={styles.statRecent}>
                      {f.lastModified ? `updated ${formatRelative(f.lastModified)}` : 'empty'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!browseLoading && browseFolders.length === 0 && photos.length === 0 && (
            <div className={styles.emptyState}>
              <p>Nothing in <strong>{selectedFolder}</strong> yet.</p>
              <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                Add files into <code>{detailData.getMediaCollection.project.properties.basePath}/{selectedFolder}/</code> via Finder, then hit Reload.
              </p>
            </div>
          )}

          <div className={styles.photoGrid}>
            {photos.map((photo, i) => (
              <div key={photo.identity} className={styles.photoCard} onClick={() => setPreviewIdx(i)}>
                <div className={styles.photoThumb}>
                  {photo.thumbnailUrl ? (
                    <img
                      src={thumbSrc(photo.thumbnailUrl)}
                      alt={photo.filename}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.opacity = 0.2 }}
                    />
                  ) : (
                    <div className={styles.photoThumbPlaceholder}>
                      <FolderIcon fontSize="small" />
                      <span>no preview</span>
                    </div>
                  )}
                </div>
                <div className={styles.photoMeta}>
                  <div className={styles.photoName} title={photo.filename}>{photo.filename}</div>
                  <div className={styles.photoStats}>
                    <span>{formatBytes(photo.size)}</span>
                    <span>{formatRelative(photo.mtime)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {previewIdx !== null && photos[previewIdx] && (
            <div className={styles.lightbox} onClick={() => setPreviewIdx(null)}>
              <button className={styles.lightboxClose} onClick={() => setPreviewIdx(null)} title="Close (Esc)">
                <CloseIcon />
              </button>
              {previewIdx > 0 && (
                <button
                  className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
                  onClick={(e) => { e.stopPropagation(); setPreviewIdx(previewIdx - 1) }}
                  title="Previous (←)">
                  <NavigateBeforeIcon />
                </button>
              )}
              <div className={styles.lightboxInner} onClick={(e) => e.stopPropagation()}>
                {photos[previewIdx].thumbnailUrl ? (
                  <img src={thumbSrc(photos[previewIdx].thumbnailUrl)} alt={photos[previewIdx].filename} />
                ) : (
                  <div className={styles.lightboxNoPreview}>No preview for this file type</div>
                )}
                <div className={styles.lightboxCaption}>
                  {photos[previewIdx].filename} · {formatBytes(photos[previewIdx].size)} · {previewIdx + 1} / {photos.length}
                </div>
              </div>
              {previewIdx < photos.length - 1 && (
                <button
                  className={`${styles.lightboxNav} ${styles.lightboxNext}`}
                  onClick={(e) => { e.stopPropagation(); setPreviewIdx(previewIdx + 1) }}
                  title="Next (→)">
                  <NavigateNextIcon />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ───────────────────── DETAIL VIEW ─────────────────────
  if (selectedId && detailData?.getMediaCollection) {
    const { project, folders } = detailData.getMediaCollection
    const p = project.properties
    const isCheckedOut = !!p.checkedOutBy
    const isCheckedOutByMe = p.checkedOutBy === username
    const isCheckedOutByOther = isCheckedOut && !isCheckedOutByMe
    const totalBytes = folders.reduce((sum, f) => sum + (f.totalBytes || 0), 0)
    const totalFiles = folders.reduce((sum, f) => sum + (f.fileCount || 0), 0)

    return (
      <div className={styles.view}>
        <div className={styles.gridBg} />
        <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>

        <div className={styles.container}>
          <header className={styles.header}>
            <button className={styles.backBtn} onClick={handleBack}>
              <ArrowBackIcon fontSize="small" /> Shoots
            </button>
            <div className={styles.titleGroup}>
              <div className={styles.label}>{p.project || 'Media'}</div>
              <h1 className={styles.title}>{p.name}</h1>
              {p.notes && <p className={styles.notes}>{p.notes}</p>}
            </div>
            <div className={styles.toolbar}>
              <button className={styles.iconBtn} title="Reload" onClick={() => refetchDetail({ identity: selectedId })}>
                <RefreshIcon fontSize="small" />
              </button>
              <button className={`${styles.iconBtn} ${styles.dangerBtn}`} title="Delete shoot" onClick={() => handleDelete(selectedId)}>
                <DeleteOutlineIcon fontSize="small" />
              </button>
            </div>
          </header>

          <div className={styles.detailMeta}>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Folder</span>
              <span className={styles.metaValue} title={p.basePath}>{p.basePath}</span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Totals</span>
              <span className={styles.metaValue}>{totalFiles.toLocaleString()} files · {formatBytes(totalBytes)}</span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Shoot date</span>
              {editingDate ? (
                <span className={styles.metaValue} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="date"
                    className={styles.input}
                    style={{ padding: '0.2rem 0.4rem', width: 'auto' }}
                    value={localDate}
                    onChange={e => setLocalDate(e.target.value)}
                    autoFocus
                  />
                  <button className={styles.iconBtn} onClick={() => handleSaveDate(selectedId)} title="Save">✓</button>
                  <button className={styles.iconBtn} onClick={() => setEditingDate(false)} title="Cancel">✕</button>
                </span>
              ) : (
                <span
                  className={styles.metaValue}
                  style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}
                  onClick={() => { setLocalDate(p.shootDate || todayISO()); setEditingDate(true) }}
                  title="Click to set the shoot date"
                >
                  {formatShootDate(p.shootDate) || 'Set date'}
                </span>
              )}
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Created by</span>
              <span className={styles.metaValue}>{p.username} · {formatRelative(p.createdAt)}</span>
            </div>
          </div>

          <div className={styles.checkoutPanel}>
            {!isCheckedOut && (
              <>
                <div className={styles.checkoutStatus}>
                  <LockOpenIcon fontSize="small" style={{ color: '#6ee7b7' }} />
                  <span>Available — no one is editing</span>
                </div>
                <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => handleCheckOut(selectedId)}>
                  <LockIcon fontSize="small" /> Check out
                </button>
              </>
            )}
            {isCheckedOutByMe && (
              <>
                <div className={styles.checkoutStatus}>
                  <LockIcon fontSize="small" style={{ color: '#fbbf24' }} />
                  <span>You have this checked out — {formatRelative(p.checkedOutAt)}</span>
                </div>
                <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => handleCheckIn(selectedId)}>
                  <LockOpenIcon fontSize="small" /> Check in
                </button>
              </>
            )}
            {isCheckedOutByOther && (
              <>
                <div className={styles.checkoutStatus}>
                  <LockIcon fontSize="small" style={{ color: '#f87171' }} />
                  <span><strong>{p.checkedOutBy}</strong> has this checked out — {formatRelative(p.checkedOutAt)}</span>
                </div>
                <button
                  className={`${styles.iconBtn} ${styles.warningBtn}`}
                  onClick={() => {
                    if (window.confirm(`${p.checkedOutBy} currently has this shoot checked out. Take it over?`)) {
                      handleCheckOut(selectedId)
                    }
                  }}>
                  <LockIcon fontSize="small" /> Take over
                </button>
                <button className={styles.iconBtn} onClick={() => handleCheckIn(selectedId)}>
                  Force check in
                </button>
              </>
            )}
          </div>

          <div className={styles.folderGrid}>
            {folders.map(f => {
              return (
                <div
                  key={f.name}
                  className={`${styles.folderCard} ${styles.folderCardBrowsable}`}
                  onClick={() => handleOpenFolder(f.path || f.name)}
                  title={`Open ${f.name}`}
                >
                  <div className={styles.folderHeader}>
                    <FolderIcon fontSize="small" style={{ color: '#94a3b8' }} />
                    <span className={styles.folderName}>{f.name}</span>
                  </div>
                  <div className={styles.folderStats}>
                    <div className={styles.statLine}>
                      <span className={styles.statNumber}>{f.fileCount.toLocaleString()}</span>
                      <span className={styles.statUnit}>files</span>
                    </div>
                    <div className={styles.statLine}>
                      <span className={styles.statNumber}>{formatBytes(f.totalBytes)}</span>
                    </div>
                    {f.lastModified && (
                      <div className={styles.statRecent}>
                        last touched {formatRelative(f.lastModified)}
                      </div>
                    )}
                    {!f.lastModified && (
                      <div className={styles.statRecent}>empty</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div className={styles.workflowHint}>
            Click a folder to open it. Import folders nest by device (Sony · iPhone · GoPro). Add files via Finder/Lightroom at the path above.
          </div>
        </div>
      </div>
    )
  }

  // ───────────────────── LIST VIEW ─────────────────────
  return (
    <div className={styles.view}>
      <div className={styles.gridBg} />
      <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>

      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Media</div>
            <h1 className={styles.title}>Shoots</h1>
          </div>
          <div className={styles.toolbar}>
            <button className={styles.iconBtn} onClick={() => setLibraryOpen(true)} title="Browse originals library">
              <PhotoLibraryIcon fontSize="small" /> Library
            </button>
            <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => setIsCreating(true)}>
              <AddIcon fontSize="small" /> New Shoot
            </button>
            <button className={styles.iconBtn} onClick={() => refetchList()} title="Reload">
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </header>

        {isCreating && (
          <form className={styles.createForm} onSubmit={handleCreate}>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Project</label>
              <input
                type="text"
                className={styles.input}
                value={newProject}
                onChange={e => setNewProject(e.target.value)}
                placeholder="e.g. CleverCar — pick existing or type a new one"
                list="media-projects"
                autoFocus
              />
              <datalist id="media-projects">
                {existingProjects.map(pr => (
                  <option key={pr.identity} value={pr.properties.title} />
                ))}
              </datalist>
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Shoot name</label>
              <input
                type="text"
                className={styles.input}
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Audi A3"
              />
              {(newProject.trim() || newName.trim()) && (
                <div className={styles.slugPreview}>
                  Folder: <code>{slugifyPreview(newProject) || '(project)'}/{slugifyPreview(newName) || '(shoot)'}</code>
                </div>
              )}
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Shoot date</label>
              <input
                type="date"
                className={styles.input}
                value={newShootDate}
                onChange={e => setNewShootDate(e.target.value)}
              />
            </div>
            <div className={styles.createField}>
              <label className={styles.fieldLabel}>Notes (optional)</label>
              <textarea
                className={styles.input}
                value={newNotes}
                onChange={e => setNewNotes(e.target.value)}
                placeholder="Client, shoot date, car spec, anything worth remembering"
                rows={2}
              />
            </div>
            {createError && <div className={styles.errorMsg}>{createError}</div>}
            <div className={styles.createActions}>
              <button type="button" className={styles.iconBtn} onClick={() => { setIsCreating(false); setNewName(''); setNewProject(''); setNewNotes(''); setCreateError(null) }}>
                Cancel
              </button>
              <button type="submit" className={`${styles.iconBtn} ${styles.primaryBtn}`} disabled={creating || !newName.trim() || !newProject.trim()}>
                {creating ? 'Creating…' : 'Create + scaffold folders'}
              </button>
            </div>
          </form>
        )}

        {listLoading && projects.length === 0 && <div className={styles.status}>Loading…</div>}
        {!listLoading && projects.length === 0 && !isCreating && (
          <div className={styles.emptyState}>
            <p>No shoots yet.</p>
            <button className={`${styles.iconBtn} ${styles.primaryBtn}`} onClick={() => setIsCreating(true)}>
              <AddIcon fontSize="small" /> Create the first shoot
            </button>
          </div>
        )}

        {groupShootsByProject(projects).map(group => (
          <div key={group.project} className={styles.projectGroup}>
            <div className={styles.projectGroupHeader}>{group.project}</div>
            <div className={styles.projectGrid}>
              {group.shoots.map(proj => {
                const pp = proj.properties
                const checkedOut = !!pp.checkedOutBy
                const byMe = pp.checkedOutBy === username
                return (
                  <div key={proj.identity} className={styles.projectCard} onClick={() => handleSelect(proj.identity)}>
                    <div className={styles.projectCardHeader}>
                      <div className={styles.projectName}>{pp.name}</div>
                      {checkedOut && (
                        <span className={`${styles.checkoutBadge} ${byMe ? styles.checkoutByMe : styles.checkoutByOther}`}>
                          <LockIcon style={{ fontSize: 12 }} />
                          {byMe ? 'you' : pp.checkedOutBy}
                        </span>
                      )}
                    </div>
                    {pp.notes && <div className={styles.projectNotes}>{pp.notes}</div>}
                    <div className={styles.projectMeta}>
                      <span className={styles.slugTag}>{pp.slug}</span>
                      <span>{formatShootDate(pp.shootDate) || formatRelative(pp.createdAt)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
